import { useState } from "react";
import {
  itemKey,
  type InventoryItem,
  type InventoryKind,
  type SaveSupplier,
  type SaveSupplierPrice,
  type Supplier,
  type SupplierPrice,
} from "@dentacore/shared-types";

import { minorUnitsPerUnit } from "../patients/money";
import { Modal } from "../ui/modal";
import { describeNumberError, NumberField } from "../ui/number-field";
import { COUNT, KINDS, USES } from "./stock";
import { typedMinorUnits } from "./supplier-text";
import inv from "./inventory.module.css";
import styles from "./suppliers.module.css";
import { RequiredMark } from "../ui/required-mark";

const DAYS = { min: 0, integer: true, unit: "days" } as const;
const NEW_ITEM = "__new";

/** Add a supplier, or edit one. */
export function SupplierDialog({
  supplier,
  busy,
  onSave,
  onClose,
  location,
}: {
  /** Where a new supplier goes, when several locations are shown. */
  location?: React.ReactNode;
  /** Null to add one. */
  supplier: Supplier | null;
  busy: boolean;
  onSave: (body: SaveSupplier) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const [name, setName] = useState(supplier?.name ?? "");
  const [phone, setPhone] = useState(supplier?.phone ?? "");
  const [email, setEmail] = useState(supplier?.email ?? "");
  const [note, setNote] = useState(supplier?.note ?? "");
  const title = supplier === null ? "Add a supplier" : "Edit supplier";

  return (
    <Modal label={title} onClose={onClose} testId="supplier-dialog">
      <form
        className={inv.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() === "") return;
          void onSave({
            name: name.trim(),
            phone,
            email: email.trim() === "" ? null : email.trim(),
            note,
          }).then((landed) => {
            if (landed) onClose();
          });
        }}
      >
        <h2 className={inv.dialogTitle}>{title}</h2>
        {location}
        <label className={inv.field}>
          <span className={inv.fieldLabel}>
            Name
            <RequiredMark />
          </span>
          <input
            className={inv.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            data-testid="supplier-name"
          />
        </label>
        <div className={inv.dialogGrid}>
          <label className={inv.field}>
            <span className={inv.fieldLabel}>Phone or WhatsApp</span>
            <input
              className={inv.input}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="supplier-phone"
            />
          </label>
          <label className={inv.field}>
            <span className={inv.fieldLabel}>Email</span>
            <input
              className={inv.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="supplier-email"
            />
          </label>
        </div>
        <label className={inv.field}>
          <span className={inv.fieldLabel}>Note for whoever orders</span>
          <textarea
            className={inv.input}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Order before noon for next-day delivery"
          />
        </label>
        <div className={inv.dialogActions}>
          <button type="button" className={inv.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={inv.primary}
            disabled={busy || name.trim() === ""}
            data-testid="supplier-save"
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * *Add an item* to a supplier's list, or edit a price — STORY-171.
 *
 * **It can create the item too** (Anthony's comment on the prototype): the
 * picker opens on "A new item, not in Inventory yet", which adds it to this
 * location's inventory in the same request (SU-D11). Its full shelf is
 * optional; without one, one pack counts as a full shelf (STORY-112's rule).
 * Picking an existing item instead adds only the price.
 */
export function PriceDialog({
  supplier,
  price,
  items,
  currency,
  busy,
  onSave,
  onRemove,
  onClose,
}: {
  supplier: Supplier;
  /** Null to add one. */
  price: SupplierPrice | null;
  /** This location's items, to pick from. */
  items: readonly InventoryItem[];
  currency: string;
  busy: boolean;
  onSave: (body: SaveSupplierPrice) => Promise<boolean>;
  onRemove: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const editing = price !== null;
  const perUnit = minorUnitsPerUnit(currency);
  const decimals = Math.round(Math.log10(perUnit));
  // Items this supplier does not list yet, so the picker cannot offer a clash.
  const listed = new Set(supplier.prices.map((p) => p.itemKey));
  const pickable = items.filter((i) => i.isActive && !listed.has(itemKey(i.name)));

  const [itemChoice, setItemChoice] = useState(editing ? price.itemName : NEW_ITEM);
  const [newName, setNewName] = useState("");
  const [kind, setKind] = useState<InventoryKind>("COUNTABLE");
  const [par, setPar] = useState("");
  // A new bulk item needs about how many uses a pack serves (MC-D4).
  const [estimate, setEstimate] = useState("");
  const [pack, setPack] = useState(price?.pack ?? "");
  const [uses, setUses] = useState(price === null ? "" : String(price.usesPerPack));
  const [amount, setAmount] = useState(
    price === null ? "" : (price.priceCents / perUnit).toFixed(decimals),
  );
  const [days, setDays] = useState(
    price?.leadDays === null || price === null ? "" : String(price.leadDays),
  );

  const isNew = !editing && itemChoice === NEW_ITEM;
  const needsEstimate = isNew && kind === "BULK";
  const priceRules = { min: 0, integer: decimals === 0, unit: currency } as const;
  const clash = isNew && items.some((i) => itemKey(i.name) === itemKey(newName));
  const blocked =
    (isNew ? newName.trim() === "" || clash || describeNumberError(par, COUNT) !== null : false) ||
    pack.trim() === "" ||
    uses.trim() === "" ||
    describeNumberError(uses, USES) !== null ||
    typedMinorUnits(amount, currency) === null ||
    describeNumberError(days, DAYS) !== null ||
    (needsEstimate && (estimate.trim() === "" || describeNumberError(estimate, USES) !== null));

  const title = editing ? `Edit ${price.itemName}` : `Add an item to ${supplier.name}`;

  return (
    <Modal label={title} onClose={onClose} testId="price-dialog">
      <form
        className={inv.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          if (blocked) return;
          void onSave({
            itemName: isNew ? newName.trim() : itemChoice,
            pack: pack.trim(),
            usesPerPack: Number(uses),
            priceCents: typedMinorUnits(amount, currency) ?? 0,
            leadDays: days.trim() === "" ? null : Number(days),
            ...(isNew
              ? {
                  createItem: {
                    kind,
                    ...(par.trim() === "" || Number(par) === 0 ? {} : { parLevel: Number(par) }),
                    ...(needsEstimate ? { estimatedUses: Number(estimate) } : {}),
                  },
                }
              : {}),
          }).then((landed) => {
            if (landed) onClose();
          });
        }}
      >
        <h2 className={inv.dialogTitle}>{title}</h2>
        {!editing && (
          <p className={styles.sub}>Pick an item already in Inventory, or add a new one.</p>
        )}

        <div className={inv.dialogGrid}>
          {!editing && (
            <label className={inv.field}>
              <span className={inv.fieldLabel}>
                Item
                <RequiredMark />
              </span>
              <select
                className={inv.input}
                value={itemChoice}
                onChange={(e) => setItemChoice(e.target.value)}
                data-testid="price-item"
              >
                <option value={NEW_ITEM}>+ A new item, not in Inventory yet</option>
                {pickable.map((i) => (
                  <option key={i.id} value={i.name}>
                    {i.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {isNew && (
            <label className={inv.field}>
              <span className={inv.fieldLabel}>
                New item&apos;s name
                <RequiredMark />
              </span>
              <input
                className={inv.input}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
                data-testid="price-new-name"
              />
              {clash && (
                <span className={styles.error}>
                  Already in Inventory — pick it from the list instead.
                </span>
              )}
            </label>
          )}
          {isNew && (
            <label className={inv.field}>
              <span className={inv.fieldLabel}>
                Kind
                <RequiredMark />
              </span>
              <select
                className={inv.input}
                value={kind}
                onChange={(e) => setKind(e.target.value as InventoryKind)}
              >
                {KINDS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {needsEstimate && (
            <NumberField
              label="About how many uses in one pack?"
              required
              className={inv.field}
              labelClassName={inv.fieldLabel}
              inputClassName={inv.input}
              value={estimate}
              onChange={setEstimate}
              rules={USES}
              hint="An estimate, for costing procedures — a tube still opens as one."
              testId="price-estimated-uses"
            />
          )}
          {isNew && (
            <NumberField
              label="A full shelf, in uses"
              className={inv.field}
              labelClassName={inv.fieldLabel}
              inputClassName={inv.input}
              value={par}
              onChange={setPar}
              rules={COUNT}
              placeholder="Blank: one pack counts as full"
              testId="price-par"
            />
          )}
          <label className={inv.field}>
            <span className={inv.fieldLabel}>
              Their pack
              <RequiredMark />
            </span>
            <input
              className={inv.input}
              value={pack}
              onChange={(e) => setPack(e.target.value)}
              placeholder="box of 100"
              data-testid="price-pack"
            />
          </label>
          <NumberField
            label="Uses in one pack"
            required
            className={inv.field}
            labelClassName={inv.fieldLabel}
            inputClassName={inv.input}
            value={uses}
            onChange={setUses}
            rules={USES}
            testId="price-uses"
          />
          <NumberField
            label="Price per pack"
            required
            className={inv.field}
            labelClassName={inv.fieldLabel}
            inputClassName={inv.input}
            value={amount}
            onChange={setAmount}
            rules={priceRules}
            hint={
              amount.trim() !== "" && typedMinorUnits(amount, currency) === null
                ? `A price above zero, with at most ${String(decimals)} decimal${decimals === 1 ? "" : "s"}.`
                : undefined
            }
            testId="price-amount"
          />
          <NumberField
            label="Days to arrive"
            className={inv.field}
            labelClassName={inv.fieldLabel}
            inputClassName={inv.input}
            value={days}
            onChange={setDays}
            rules={DAYS}
            testId="price-days"
          />
        </div>

        {isNew && (
          <p className={styles.note}>
            The item is added to this location&apos;s inventory with nothing on the shelf.
            {par.trim() === "" &&
              " Without a full shelf, one pack counts as full and it warns at a quarter of that."}
          </p>
        )}

        <div className={inv.dialogActions}>
          {editing && (
            <button
              type="button"
              className={`${inv.danger} ${styles.pushLeft}`}
              onClick={onRemove}
              disabled={busy}
            >
              Take off their list
            </button>
          )}
          <button type="button" className={inv.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={inv.primary}
            disabled={busy || blocked}
            data-testid="price-save"
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
